/**
 * HITL API client — Sprint 2.A · MV5 Wave 2.
 *
 * Thin axios wrappers for the 9 instructor-facing HITL endpoints on
 * session-manager. Types mirror the service payloads in
 * services/session-manager/src/services/HitlService.ts.
 */

import { sessionManagerApi } from './apiClient';

// ── Types ───────────────────────────────────────────────────────────────────

export type EscalationStatus = 'waiting' | 'assigned' | 'resolved' | 'cancelled';
export type EscalationSeverity = 'low' | 'medium' | 'high';
export type SessionStatus = 'active' | 'ended' | 'abandoned';
export type MessageSender = 'learner' | 'instructor' | 'ai';
export const HITL_MESSAGE_MAX_LENGTH = 4_000;
export const HITL_MESSAGE_MAX_BYTES = 8_000;
export const HITL_ANNOTATION_MAX_LENGTH = 1_000;

export interface Escalation {
  id: string;
  learner_id: string;
  source_lecture_id: string | null;
  source_ai_conversation_id: string | null;
  code_snapshot: Record<string, unknown> | null;
  learner_note: string | null;
  severity: EscalationSeverity;
  status: EscalationStatus;
  requested_at: string;
  wait_time_ms: number | null;
  resolved_at: string | null;
  created_at: string;
  session_id?: string | null;
  instructor_id?: string | null;
}

export interface SessionRow {
  id: string;
  escalation_id: string;
  instructor_id: string;
  taken_at: string;
  handback_at: string | null;
  session_length_ms: number | null;
  status: SessionStatus;
  created_at: string;
}

export interface HitlMessage {
  id: string;
  session_id: string;
  sender_type: MessageSender;
  content: string;
  sent_at: string;
  sequence: number;
  client_message_id?: string | null;
}

export interface Annotation {
  id: string;
  session_id: string;
  line_range_start: number;
  line_range_end: number;
  note: string;
  file_path: string | null;
  created_at: string;
}

export interface CoveredChip {
  slug: string;
  label: string;
}

export interface Handback {
  session_id: string;
  covered_chips: CoveredChip[];
  watch_for_note: string | null;
  learner_confidence_rating: number | null;
  created_at: string;
}

export interface Report {
  session_id: string;
  ai_missed_note: string | null;
  content_team_review_status: 'pending' | 'reviewed' | 'resolved';
  reviewed_at: string | null;
  created_at: string;
}

export interface InstructorPreferences {
  instructor_id: string;
  wait_threshold_min: number;
  max_concurrent: number;
  hours_start: string | null;
  hours_end: string | null;
  timezone: string;
  notify_when_ready: boolean;
  created_at: string;
  updated_at: string;
}

export interface SessionContext {
  session: SessionRow;
  escalation: Escalation;
  learner: { id: string; email: string; first_name: string | null; last_name: string | null };
  messages: HitlMessage[];
  annotations: Annotation[];
  ai_history: { role: string; content: string; created_at: string }[];
}

export function createClientMessageId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(bytes);
  if (bytes.every((value) => value === 0)) {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join(''),
  ].join('-');
}

function utf8BytesForCodePoint(value: string): number {
  const codePoint = value.codePointAt(0) ?? 0;
  if (codePoint <= 0x7f) return 1;
  if (codePoint <= 0x7ff) return 2;
  if (codePoint <= 0xffff) return 3;
  return 4;
}

export function truncateUtf8(value: string, maxBytes: number): string {
  if (maxBytes <= 0 || value.length === 0) return '';
  let bytes = 0;
  const characters: string[] = [];
  for (const character of Array.from(value)) {
    const nextBytes = utf8BytesForCodePoint(character);
    if (bytes + nextBytes > maxBytes) break;
    bytes += nextBytes;
    characters.push(character);
  }
  return characters.join('');
}

export function truncateCodePoints(value: string, maxCharacters: number): string {
  if (maxCharacters <= 0 || value.length === 0) return '';
  const characters: string[] = [];
  for (const character of Array.from(value)) {
    if (characters.length >= maxCharacters) break;
    characters.push(character);
  }
  return characters.join('');
}

// ── API ─────────────────────────────────────────────────────────────────────

export const hitlApi = {
  async listEscalations(filter: 'all' | 'assigned' | string = 'all'): Promise<Escalation[]> {
    const res = await sessionManagerApi.get('/hitl/escalations', { params: { filter } });
    return res.data.escalations as Escalation[];
  },

  async takeEscalation(escalationId: string): Promise<{ session: SessionRow; escalation: Escalation }> {
    const res = await sessionManagerApi.post(`/hitl/escalations/${escalationId}/take`);
    return res.data;
  },

  async getContext(sessionId: string): Promise<SessionContext> {
    const res = await sessionManagerApi.get(`/hitl/sessions/${sessionId}/context`);
    return res.data as SessionContext;
  },

  async sendMessage(
    sessionId: string,
    content: string,
    clientMessageId: string = createClientMessageId(),
  ): Promise<HitlMessage> {
    const res = await sessionManagerApi.post(`/hitl/sessions/${sessionId}/messages`, {
      content: truncateUtf8(
        truncateCodePoints(content.trim(), HITL_MESSAGE_MAX_LENGTH),
        HITL_MESSAGE_MAX_BYTES,
      ),
      client_message_id: clientMessageId,
    });
    return res.data.message as HitlMessage;
  },

  async addAnnotation(input: {
    session_id: string;
    line_range_start: number;
    line_range_end: number;
    note: string;
    file_path?: string | null;
  }): Promise<Annotation> {
    const res = await sessionManagerApi.post(
      `/hitl/sessions/${input.session_id}/annotations`,
      input,
    );
    return res.data.annotation as Annotation;
  },

  async handback(input: {
    session_id: string;
    covered_chips: CoveredChip[];
    watch_for_note?: string;
    learner_confidence_rating?: number;
  }): Promise<{ session: SessionRow; handback: Handback }> {
    const res = await sessionManagerApi.post(
      `/hitl/sessions/${input.session_id}/handback`,
      input,
    );
    return res.data;
  },

  async submitReport(input: { session_id: string; ai_missed_note?: string }): Promise<Report> {
    const res = await sessionManagerApi.post('/hitl/reports', input);
    return res.data.report as Report;
  },

  async getPreferences(): Promise<InstructorPreferences> {
    const res = await sessionManagerApi.get('/hitl/instructors/me/preferences');
    return res.data.preferences as InstructorPreferences;
  },

  async updatePreferences(patch: Partial<InstructorPreferences>): Promise<InstructorPreferences> {
    const res = await sessionManagerApi.patch('/hitl/instructors/me/preferences', patch);
    return res.data.preferences as InstructorPreferences;
  },
};
